import { Component, computed, input, output } from '@angular/core';
import { ProductKeys } from '@config/keys/product.keys';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { DoodProductModel } from '@core/models/product.model';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { hasDiscountPromotionLabel } from '@core/services/eligible-promotions-labels/has-discount-promotion-label.util';
import { TranslatePipe } from '@ngx-translate/core';
import { PriceAtomComponent } from '@shared/atoms/price-atom/price-atom.component';
import { TagAtomComponent } from '@shared/atoms/tag-atom/tag-atom.component';
import { RadialGradientDirective } from '@shared/directives/radial-gradient.directive';
import { VisibleLabelsPipe } from '@shared/pipes/visible-product-labels/visible-product-labels.pipe';
import { fromContentBuilderColor } from '@shared/utils/content-builder-color/content-builder-color';
import { KioskProductCardInputs } from '../kiosk-product-card-factory/kiosk-product-cart';

@Component({
  selector: 'app-kiosk-product-card-v5-atom',
  templateUrl: './kiosk-product-card-v5-atom.component.html',
  styleUrls: ['./kiosk-product-card-v5-atom.component.scss'],
  imports: [
    ContentBuilderColorPipe,
    TranslatePipe,
    PriceAtomComponent,
    TagAtomComponent,
    VisibleLabelsPipe,
    RadialGradientDirective,
  ],
  styles: `
    :host {
      @apply h-full;
      img {
        @apply rounded-t;
      }
    }
    :host.borderedCard {
      @apply rounded-xl border border-solid border-neutral-300 bg-neutral-50 p-1;
      > div {
        @apply p-1 rounded-xl;
        .imageContainer {
          @apply rounded-t-xl;
        }
      }
    }

    :host.shadowCard {
      box-shadow: var(--kiosk-card-shadow);
      @apply rounded p-2;
    }

    :host.roundedCard {
      @apply rounded-xl;
      > div {
        @apply rounded-xl;
        .imageContainer {
          @apply rounded-t-xl;
        }
      }
    }
  `,
  // Host paints the card box (factory uses display:contents). Also overrides
  // borderedCard's bg-neutral-50 / shadowCard padding that template can't cover.
  host: {
    '[style.background-color]': '_cardColor() || "#ffffff"',
  },
})
export class KioskProductCardV5AtomComponent implements KioskProductCardInputs {
  readonly product = input.required<DoodProductModel>();
  readonly quantity = input.required<number | undefined>();
  readonly isProductAvailable = input<boolean | undefined>(true);
  readonly textColor = input<IContentBuilderFieldColor>();
  readonly cardColor = input<IContentBuilderFieldColor>();
  readonly _cardColor = computed(() => fromContentBuilderColor(this.cardColor(), '#ffffff'));
  readonly buttonBackgroundColor = input<IContentBuilderFieldColor>();
  readonly unavailableButtonBackground = input<IContentBuilderFieldColor>();
  readonly unavailableButtonText = input<IContentBuilderFieldColor>();
  readonly buttonTextColor = input<IContentBuilderFieldColor>();
  readonly priceColor = input<IContentBuilderFieldColor>();
  readonly isShowProductIcon = input<boolean>(true);

  readonly addProduct = output<void>();
  readonly showProduct = output<void>();

  public get ProductKeys(): typeof ProductKeys {
    return ProductKeys;
  }

  displayDiscount = computed(() => {
    const product = this.product();
    const discount = product[ProductKeys.Discount] ?? 0;
    return discount > 0;
  });

  discountPercentage = computed(() => {
    const product = this.product();
    const originalPrice = product[ProductKeys.Price];
    const discount = product[ProductKeys.Discount] ?? 0;

    if (originalPrice <= 0 || discount <= 0 || originalPrice <= discount) {
      return 0;
    }

    const discountAmount = originalPrice - discount;
    const discountPercentage = Math.round((discountAmount / originalPrice) * 100);
    return discountPercentage;
  });

  addToCart(): void {
    this.addProduct.emit();
  }

  showProductModal(): void {
    this.showProduct.emit();
  }

  protected readonly _hasDiscountPromotionLabel = computed(() =>
    hasDiscountPromotionLabel(this.product()),
  );
}
